import { Component, Input } from '@angular/core';
import { ThesaurusEntry } from '@interfaces/thesaurus-entry.interface';
import { ThesaurusPath } from '@interfaces/thesaurus-path.interface';
import { ThesaurusSelectedTerm } from '@interfaces/thesaurus-selected-term.interface';
import { TermsService } from '@terms-service/terms.service';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import * as bootstrap from 'bootstrap';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-modal',
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.scss',
    standalone: false
})
export class ModalComponent {
  @Input() modalId: string = "";
  @Input() thesaurusId: string = "";
  nativeElement: any;
  @Input() entries?: ThesaurusEntry[];
  terms?: ThesaurusSelectedTerm[];
  @Input() explode?: boolean;
  @Input() major?: boolean;
  cleared: boolean = false;
  private termsSubscription!: Subscription;
  majorColumnLabel: string = "Major";
  explodeColumnLabel: string = "Explode";
  mainTerm: string = "main";
  majorType: string = "major";
  explodeType: string = "explode";
  relatedTerm: string = "related";
  qualifierTerm: string = "qualifier";
  modalBodyClass: string = ".modal-body";

  constructor(
    public termsService: TermsService,
    public thesaurusService: ThesaurusService
  ) {}

  ngOnInit(): void {
    this.termsSubscription = this.termsService.allCheckedEntryNames$.subscribe(terms => {
      this.terms = terms;
    });
  }

  ngOnDestroy(): void {
    if (this.termsSubscription) {
      this.termsSubscription.unsubscribe();
    }
  }

    openModal(event: Event) {
      event.preventDefault();
      const myModal = document.getElementById(this.modalId);

      if (myModal) {
        const modalInstance = new bootstrap.Modal(myModal, {
          backdrop: false
        });
        modalInstance.show();

        const modalBody = document.querySelector(this.modalBodyClass);
        const modalInputs = modalBody?.querySelectorAll('input');
        modalInputs?.forEach((input: HTMLInputElement) => {
          let name: string = input.value;
        });

        this.toggleDisabledClassForText();
      }
    }

    closeModal(event: Event) {
      event.preventDefault();
      const myModal = document.getElementById(this.modalId);

      if (myModal) {
        const modalInstance = bootstrap.Modal.getInstance(myModal);
        if (modalInstance) {
          modalInstance.hide();
        }

        //If terms have been cleared, remove them from the modal after closing
        if (this.cleared) {
          this.termsService.clearList();
          this.cleared = false;
        }
      }

      if (this.elementsAreDisabled()) {
        this.toggleDisabledClassForText();
      }
      //this.toggleDisabledClassForText();
    }
  
    elementsAreDisabled(): boolean {
      const disabledCheckboxes = document.querySelectorAll('.disabled-checkbox');
      const disabledTextElements = document.querySelectorAll('.disabled-text');
      const disabledIcons = document.querySelectorAll('.disabled-icon');

      return disabledCheckboxes.length > 0 || disabledTextElements.length > 0 || disabledIcons.length > 0;
    }

    isRelatedTerm(name: string): boolean {
      return this.termsService.isRelatedTerm(name);
    }

    isSelectedTermsModal(): boolean {
      return this.modalId == 'selectedTermsModal';
    }

    getModalLabel(): string {
      return this.modalId + "Label";
    }

  //Use Case: when qualifier is checked but its main/parent term is unchecked, add disabled class to the main term (and its associated major/explode checkboxes)
  private disableQualifierParentTerm() {
    const parentTermsWithCheckbox = document.querySelectorAll('.hasOnlyQualifiers');
    parentTermsWithCheckbox.forEach((element) => {
      const input = element as HTMLInputElement;
      const id = input.id;
      const className = input.className;
      const match = id.match(/modal-term-main-(\d+)$/);
      const index = match ? match[1] : null;
      const explodeCheckbox = document.getElementById('modal-term-explode-' + index);
      const majorCheckbox = document.getElementById('modal-term-major-' + index);

      if (input && !input.checked) {
        // If the label is immediately after the input
        const label = input.nextElementSibling as HTMLLabelElement | null;
        if (label && label.tagName.toLowerCase() === 'label') {
          label.classList.toggle('disabled-text');
          input.disabled = true;
          input.classList.toggle('disabled-checkbox');

          if (explodeCheckbox)  {
            const el = (explodeCheckbox as HTMLInputElement);
            el.disabled = !el.disabled; 
            el.classList.toggle('disabled-checkbox');
          }

          if (majorCheckbox) { 
            const el = (majorCheckbox as HTMLInputElement);
            el.disabled = !el.disabled;
            el.classList.toggle('disabled-checkbox');
          }
        }
      }
    });
  }

  private toggleDisabledClassForTextElements() {
    const elements = document.querySelectorAll('.main-term-wrapper label, .main-term-wrapper a, #broaderTerms > h4, #narrowerTerms > h4, [id^="entry-notes"], [id^="entry-qualifier-notes"], .collapsing-related-term, .explode-major-col-wrapper, .viewToggleButtons label, .entry-not-linkable, .icon-legend');
    elements.forEach((element) => {
      (element as HTMLElement).classList.toggle('disabled-text');
    });
  }

  private toggleDisabledClassForIcons() {
    const icons = document.querySelectorAll('[id^="chevron-double"], .stickies-icon, .funnel-icon');
    icons.forEach((icon) => {
      (icon as SVGElement).classList.toggle('disabled-icon');
    });
  }

  private disableInputsOutsideModal() {
    const checkboxes = document.querySelectorAll('input:not(#selectedTermsModal input)');
    checkboxes.forEach((box) => {
      const el = (box as HTMLInputElement);
      el.disabled = !el.disabled;
      el.classList.toggle('disabled-checkbox');
    });
  }

  toggleDisabledClassForText() {  
    this.disableQualifierParentTerm();
    this.toggleDisabledClassForTextElements();
    this.toggleDisabledClassForIcons();
    this.disableInputsOutsideModal();
  }

  //Uncheck all checkboxes in the modal and the main list of terms when the "Clear All" button is clicked. Terms will appear inside the modal until it's closed.
  clearSelectedTerms(event: Event) {
    event.preventDefault();
    this.cleared = true;

    //Clear checkboxes in the modal
    const modalBody = document.querySelector(this.modalBodyClass);
    const modalInputs = modalBody?.querySelectorAll('input');
    modalInputs?.forEach((input: HTMLInputElement) => {
      input.checked = false;
    });

    //Clear checkboxes in the main list
    const browseTerms = document.querySelector('#termsWrapper');
    const inputs = browseTerms?.querySelectorAll('input');
    inputs?.forEach((input: HTMLInputElement) => {
      input.checked = false;
    });
  }

  onQualifierCheckboxChange(event: Event, name: string, qualIndex: number, parentName: string): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    
    this.termsService.updateCheckedState(name, this.qualifierTerm, isChecked, [], qualIndex, parentName);
  }

  onCheckboxChange(event: Event, name: string, paths?: ThesaurusPath[]): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.termsService.updateCheckedState(name, this.mainTerm, isChecked, paths);
    //When the main checkbox is unchecked, uncheck the Explode and Major checkboxes
    if (!isChecked) {
      this.termsService.updateCheckedState(name, this.explodeType, isChecked, paths);
      this.termsService.updateCheckedState(name, this.majorType, isChecked, paths);
      this.updateItemCheckboxes(checkbox.id.toString());
    }
  }

  onExplodeCheckboxChange(event: Event, name: string, paths?: ThesaurusPath[]): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.termsService.updateCheckedState(name, this.explodeType, isChecked, paths);
    //When the Explode checkbox is selected but the "main" checkbox is unchecked, set the "checked" state of the "main" checkbox to true
    if (!this.termsService.isChecked(name, paths) && isChecked) {
      this.termsService.updateCheckedState(name, this.mainTerm, isChecked, paths);
    }
  }

  onMajorCheckboxChange(event: Event, name: string, paths?: ThesaurusPath[]): void {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;
    
    this.termsService.updateCheckedState(name, this.majorType, isChecked, paths);
    //When the Major checkbox is selected but the "main" checkbox is unchecked, set the "checked" state of the "main" checkbox to true
    if (!this.termsService.isChecked(name, paths) && isChecked) {
      this.termsService.updateCheckedState(name, this.mainTerm, isChecked, paths);
    }
  }

  onRelatedCheckboxChange(event: Event, name: string, paths?: ThesaurusPath[]): void {
  const checkbox = event.target as HTMLInputElement;
  const isChecked = checkbox.checked;

  //In MeSH, a "related" checkbox can have accompaning "explode" and "major" checkboxes in the modal. In this edge case, explode and major should be unchecked when the "related" checkbox is unchecked inside the modal.
  if (this.thesaurusService.isThesaurusWithRoot(this.thesaurusId) && !isChecked) { //'medline'
      this.termsService.updateCheckedState(name, this.explodeType, isChecked, paths);
      this.termsService.updateCheckedState(name, this.majorType, isChecked, paths);
      this.updateItemCheckboxes(checkbox.id.toString());
  }

  this.termsService.updateCheckedState(name, this.relatedTerm, isChecked, paths);
}

updateItemCheckboxes(checkBoxId: string) {
  const id = checkBoxId.substring(checkBoxId.lastIndexOf("-") + 1, checkBoxId.length);
  const wrapperId = 'modal-term-wrapper-' + id;
  const row = document.getElementById(wrapperId);
     const inputs = row?.querySelectorAll('input');
    inputs?.forEach((input: HTMLInputElement) => {
      input.checked = false;
    });
}

getPathString(path: ThesaurusPath, term: ThesaurusSelectedTerm): string {
  if (path.terms.length == 1) {
    return "(" + path.terms[0].name + ")";
  }
  let pathString = "(" + path.terms[1].name;
  if (path.terms.length == 2) {
    return pathString + ")";
  }
  else if (path.terms.length == 3) {
    return pathString + " > " + path.terms[2].name + ")";
  }
  else {
    return pathString + ">...>" + path.terms[path.terms.length -1].name + ")";
  }
}

  isSingleInstanceSelected(term : ThesaurusSelectedTerm): boolean {
    return ((this.terms?.filter(t => t.name === term.name)??[]).length <= 1);
    
  }

  unSelectedParentTermOfQualifier(termName: string, termPaths?: ThesaurusPath[]): string {
    if (this.termsService.hasQualifiers(termName) && termPaths && !this.termsService.isChecked(termName, termPaths))
      return "hasOnlyQualifiers";
    else
      return "";
    }
}